import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { EnvelopeSimple, MapPin, FacebookLogo, InstagramLogo, Gift, Handshake, Megaphone } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Reveal, Overline } from "./Reveal";
import { api } from "@/lib/api";

const FB = "https://www.facebook.com/share/1AZs3Me9tH/";
const EMAIL = "epicmemoriesllc23@gmail.com";
const inputCls = "bg-black/40 border-white/15";
const F = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-zinc-400">{label}</Label>{children}</div>);

/* ---------- Extras: Gift cards, Vendor, Sponsor ---------- */
export const Extras = () => {
  const [gift, setGift] = useState(false);
  const [partner, setPartner] = useState(null); // "vendor" | "sponsor"
  const cards = [
    { icon: Gift, t: "Gift Cards", d: "Give the gift of an epic experience.", cta: "Buy a Gift Card", onClick: () => setGift(true), testid: "extra-gift" },
    { icon: Handshake, t: "Vendor Partnership", d: "Collaborate with us on unforgettable events.", cta: "Become a Vendor", onClick: () => setPartner("vendor"), testid: "extra-vendor" },
    { icon: Megaphone, t: "Sponsorship", d: "Put your brand at the heart of the celebration.", cta: "Sponsor an Event", onClick: () => setPartner("sponsor"), testid: "extra-sponsor" },
  ];
  return (
    <section id="partners" className="relative py-24 md:py-32 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal><Overline>Partner With Us</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-heading font-black text-3xl md:text-5xl mt-4">Grow with <span className="gold-text">Epic Memories</span>.</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {cards.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.08}>
              <div className="h-full bg-[#141414] border border-white/10 rounded-lg p-8 hover-lift flex flex-col">
                <c.icon size={40} weight="duotone" className="text-[#D4AF37]" />
                <h3 className="font-heading font-bold text-2xl mt-5">{c.t}</h3>
                <p className="text-zinc-400 mt-2 flex-1">{c.d}</p>
                <button data-testid={c.testid} onClick={c.onClick}
                  className="mt-6 self-start border border-white/25 rounded-full px-5 py-2.5 font-heading font-bold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">{c.cta}</button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <GiftDialog open={gift} setOpen={setGift} />
      <PartnerDialog kind={partner} setKind={setPartner} />
    </section>
  );
};

const GiftDialog = ({ open, setOpen }) => {
  const empty = { purchaser_name: "", purchaser_email: "", recipient_name: "", recipient_email: "", amount: "", message: "" };
  const [f, setF] = useState(empty); const [l, setL] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.purchaser_name || !f.purchaser_email || !f.recipient_name || !f.amount) return toast.error("Please complete the required fields.");
    setL(true);
    try { await api.giftCard(f); toast.success("Gift card request sent! We'll email you the details."); setF(empty); setOpen(false); }
    catch { toast.error("Something went wrong."); } finally { setL(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="gift-dialog" className="bg-[#0f0f0f] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-3xl">Gift an <span className="gold-text italic">Epic</span> Experience</DialogTitle>
          <DialogDescription className="text-zinc-400">We&apos;ll follow up to arrange your gift card.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Your Name *"><Input data-testid="gift-purchaser" value={f.purchaser_name} onChange={s("purchaser_name")} className={inputCls} /></F>
            <F label="Your Email *"><Input data-testid="gift-email" type="email" value={f.purchaser_email} onChange={s("purchaser_email")} className={inputCls} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Recipient Name *"><Input value={f.recipient_name} onChange={s("recipient_name")} className={inputCls} /></F>
            <F label="Recipient Email"><Input type="email" value={f.recipient_email} onChange={s("recipient_email")} className={inputCls} /></F>
          </div>
          <F label="Amount (USD) *"><Input data-testid="gift-amount" value={f.amount} onChange={s("amount")} className={inputCls} placeholder="e.g. 100" /></F>
          <F label="Message"><Textarea value={f.message} onChange={s("message")} className={inputCls} rows={3} /></F>
          <button data-testid="gift-submit" disabled={l} className="w-full bg-[#D4AF37] text-black font-heading font-bold rounded-full py-3.5 hover:bg-[#F3E5AB] transition-colors disabled:opacity-60">{l ? "Sending…" : "Request Gift Card"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PartnerDialog = ({ kind, setKind }) => {
  const empty = { name: "", email: "", company: "", phone: "", details: "" };
  const [f, setF] = useState(empty); const [l, setL] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) return toast.error("Please add your name and email.");
    setL(true);
    try { await api.partner({ kind, ...f }); toast.success("Inquiry sent! We'll be in touch soon."); setF(empty); setKind(null); }
    catch { toast.error("Something went wrong."); } finally { setL(false); }
  };
  const label = kind === "vendor" ? "Vendor Partnership" : "Sponsorship";
  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && setKind(null)}>
      <DialogContent data-testid="partner-dialog" className="bg-[#0f0f0f] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-3xl gold-text italic">{label}</DialogTitle>
          <DialogDescription className="text-zinc-400">Tell us about you — let&apos;s create something epic together.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Name *"><Input data-testid="partner-name" value={f.name} onChange={s("name")} className={inputCls} /></F>
            <F label="Email *"><Input data-testid="partner-email" type="email" value={f.email} onChange={s("email")} className={inputCls} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Company"><Input value={f.company} onChange={s("company")} className={inputCls} /></F>
            <F label="Phone"><Input value={f.phone} onChange={s("phone")} className={inputCls} /></F>
          </div>
          <F label="Details"><Textarea value={f.details} onChange={s("details")} className={inputCls} rows={3} /></F>
          <button data-testid="partner-submit" disabled={l} className="w-full bg-[#D4AF37] text-black font-heading font-bold rounded-full py-3.5 hover:bg-[#F3E5AB] transition-colors disabled:opacity-60">{l ? "Sending…" : `Submit ${label} Inquiry`}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Contact + Newsletter + Footer ---------- */
export const Contact = () => {
  const empty = { name: "", email: "", phone: "", message: "" };
  const [f, setF] = useState(empty); const [l, setL] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error("Please fill in name, email and message.");
    setL(true);
    try { await api.contact(f); toast.success("Message sent! We'll get back to you soon."); setF(empty); }
    catch { toast.error("Something went wrong."); } finally { setL(false); }
  };
  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden">
      <div className="absolute -top-20 right-0 w-[50vw] h-[50vh] purple-glow" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><Overline>Get In Touch</Overline></Reveal>
          <Reveal delay={0.05}><h2 className="font-display text-4xl md:text-6xl mt-5 leading-[1.05]">Let&apos;s create your <span className="italic gold-text">next epic memory</span>.</h2></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 space-y-5">
              <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="flex items-center gap-4 group">
                <span className="h-12 w-12 rounded-full border border-white/15 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors"><EnvelopeSimple size={20} className="text-[#D4AF37]" /></span>
                <span className="text-lg group-hover:text-[#D4AF37] transition-colors">{EMAIL}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="h-12 w-12 rounded-full border border-white/15 flex items-center justify-center"><MapPin size={20} className="text-[#D4AF37]" /></span>
                <span className="text-lg">Little Rock, Arkansas</span>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <a data-testid="social-facebook" href={FB} target="_blank" rel="noreferrer" className="h-12 w-12 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><FacebookLogo size={22} weight="fill" /></a>
                <a data-testid="social-instagram" href={FB} target="_blank" rel="noreferrer" className="h-12 w-12 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><InstagramLogo size={22} weight="fill" /></a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="bg-[#121212] border border-white/10 rounded-lg p-7 md:p-9 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Name *"><Input data-testid="contact-name" value={f.name} onChange={s("name")} className={inputCls} /></F>
              <F label="Phone"><Input data-testid="contact-phone" value={f.phone} onChange={s("phone")} className={inputCls} /></F>
            </div>
            <F label="Email *"><Input data-testid="contact-email-input" type="email" value={f.email} onChange={s("email")} className={inputCls} /></F>
            <F label="Message *"><Textarea data-testid="contact-message" value={f.message} onChange={s("message")} className={inputCls} rows={5} /></F>
            <button data-testid="contact-submit" disabled={l} className="w-full bg-[#D4AF37] text-black font-heading font-bold rounded-full py-3.5 hover:bg-[#F3E5AB] transition-colors disabled:opacity-60">{l ? "Sending…" : "Send Message"}</button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export const Footer = () => {
  const [email, setEmail] = useState(""); const [l, setL] = useState(false);
  const sub = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email.");
    setL(true);
    try { await api.newsletter({ email }); toast.success("You're on the list! 🎉"); setEmail(""); }
    catch { toast.error("Something went wrong."); } finally { setL(false); }
  };
  return (
    <footer className="bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-center border-b border-white/10 pb-12">
          <div>
            <h3 className="font-heading font-black text-2xl md:text-3xl">Join the <span className="gold-text">Epic list</span>.</h3>
            <p className="text-zinc-400 mt-2">Get first access to events, ticket drops & party inspo.</p>
          </div>
          <form onSubmit={sub} className="flex gap-3">
            <Input data-testid="newsletter-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="bg-[#141414] border-white/15 rounded-full px-5 h-12" />
            <button data-testid="newsletter-submit" disabled={l} className="shrink-0 bg-[#D4AF37] text-black font-heading font-bold rounded-full px-6 h-12 hover:bg-[#F3E5AB] transition-colors disabled:opacity-60">{l ? "…" : "Subscribe"}</button>
          </form>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pt-10">
          <div>
            <p className="font-display italic text-2xl gold-text">Epic Memories LLC</p>
            <p className="text-zinc-500 mt-1 text-sm">Not Just Creating Memories… Creating Epic Memories.</p>
          </div>
          <p className="text-zinc-600 text-sm">© {new Date().getFullYear()} Epic Memories LLC · Little Rock, Arkansas</p>
        </div>
      </div>
    </footer>
  );
};
